import _ from 'lodash';
import { getKnowledgeItemById } from './knowledge.selector';
import { FAVORITES_ENTITIES } from '../../constants';
import { getContactItemById } from './contact.selector';
import workspaceService from '../../services/workspace.service';

const FAVORITES_ENTITY_SELECTOR_MAPPER = {
  [FAVORITES_ENTITIES.KNOWLEDGE]: {
    selector: getKnowledgeItemById,
  },
  [FAVORITES_ENTITIES.CONTACT]: {
    selector: getContactItemById,
  },
  [FAVORITES_ENTITIES.USER]: {
    selector: getContactItemById,
  },
};

export const isItemFavorite = (favoritesState, { id: itemId }) =>
  !_.isUndefined(favoritesState.byId[itemId]) &&
  _.isUndefined(favoritesState.byId[itemId].toDelete);

/**
 * The favorites as list rows: contacts and users from their slices, knowledge items
 * from `knowledge.itemsById` (the store catalog, or a by-id lookup passed in its place).
 * A favorite whose row is not loaded is left out.
 *
 * @param {object} state - `{ favorites, knowledge, contacts, usersManagement }`.
 * @param {string} [workspaceId] - Scope for knowledge favorites. Default: the selected workspace.
 * @returns {Array<Record<string, any>>} Rows, each with `entity`; `[]` before any favorite is loaded.
 * @example
 * getFavoritesItems({ favorites, knowledge: { itemsById }, contacts, usersManagement }, workspaceId);
 */
export const getFavoritesItems = (
  { favorites, knowledge, contacts, usersManagement },
  workspaceId = workspaceService.getSelectedWorkspaceId(),
) => {
  const items = [];

  if (_.isEmpty(favorites.byId)) {
    return items;
  }

  Object.keys(favorites.byId).forEach(id => {
    if (favorites.byId[id].toDelete) {
      return;
    }

    const item = getFavoritesItemByEntityAndId(
      { favorites, knowledge, contacts, usersManagement },
      { entity: favorites.byId[id].entity, itemId: favorites.byId[id].itemId },
    );

    const favorite = favorites.byId[id];
    const isUserOrContact =
      favorite.entity === FAVORITES_ENTITIES.USER || favorite.entity === FAVORITES_ENTITIES.CONTACT;

    if (item && (isUserOrContact || favorite.workspaceId === workspaceId)) {
      items.push({ ...item, entity: favorite.entity });
    }
  });

  return items;
};

/**
 * Ids of the knowledge items favorited in a workspace, for the on-demand row
 * fetch. Contacts and users are not knowledge items, so they are left out; a
 * favorite marked for removal is already gone for the user.
 *
 * @param {object} favorites - The favorites slice.
 * @param {string} [workspaceId] - Default: the selected workspace.
 * @returns {string[]} Item ids; `[]` before the favorites load.
 * @example
 * const { itemsById } = useKnowledgeItemsById(getFavoriteKnowledgeItemIds(favorites, workspaceId));
 */
export const getFavoriteKnowledgeItemIds = (
  favorites,
  workspaceId = workspaceService.getSelectedWorkspaceId(),
) => {
  return Object.values(favorites.byId || {})
    .filter(
      favorite =>
        favorite.entity === FAVORITES_ENTITIES.KNOWLEDGE &&
        !favorite.toDelete &&
        favorite.workspaceId === workspaceId,
    )
    .map(favorite => favorite.itemId);
};

export const getFavoriteByItemId = (favorites, { itemId }) => favorites.byId[itemId];

export const getFavoritesItemByEntityAndId = (state, { entity, itemId }) => {
  const { selector } = FAVORITES_ENTITY_SELECTOR_MAPPER[entity];
  return selector(state, itemId) || null;
};
